import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from "react-helmet-async";
import { Container, Grid, Image, Title, Text, Box, Space, Loader } from '@mantine/core';
import SanitizeHtmlComponent from './SanitizeHtmlComponent';
import { IMAGE_BASE_URL } from '../services/paths';
import { makeGetRequest, makePostRequest } from '../services/httpHelper';
import ErrorHandler from '../services/ErrorHandler';
import { isErrorObject } from '../utils/utils';
import './Post.css';
import { BlogPost } from '../types';
import { PostComment, PostInteraction } from './PostInterraction';




const Post: React.FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const currentUrl = window.location.href;
  
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [postRes,viewsRes, relatedRes] = await Promise.all([
        makeGetRequest(`blog-post/${postId}`),
        makePostRequest(`blog-post/${postId}/views`,{}),
        makeGetRequest(`related-posts/${postId}`)
      ]);
      
      if (isErrorObject(postRes)) throw new Error(ErrorHandler(postRes));
      if (isErrorObject(relatedRes)) throw new Error(ErrorHandler(relatedRes));

      console.log(viewsRes.data)
      
      setPost(postRes.data);
      const relatedPosts = relatedRes.data.filter((p:BlogPost)=>p.id!==postId)
      setRelatedPosts(relatedPosts);
      setError(null);
    } catch (err:any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const extractDescription = (html: string, wordLimit: number): string => {
    const textContent = new DOMParser().parseFromString(html, 'text/html').body.textContent || "";
    return textContent.split(' ').slice(0, wordLimit).join(' ') + '...';
  };

  const metaDesc = post ? extractDescription(post.content, 20) : '';
  const metaLink = `/blog/${post?.slug}/${postId}`;
  const metaImage = post?.featured_image ? `${IMAGE_BASE_URL}${post.featured_image}` : '';

  return (
    <>
      <Helmet>
        <title>{post?.title || 'FundReach'}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={metaLink} />
        <meta property="og:title" content={post?.title || ''} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:image" content={metaImage} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:type" content="article" />
      </Helmet>
      <Container className="post-wrapper" size="md">
        {isLoading && <Loader />}
        {error && <Text color="red">{error}</Text>}
        {post && (
          <>
            {post.featured_image && (
              <Box className="featured-image">
                <Image src={`${post.featured_image}`} alt={post.title} className='post-img' />
              </Box>
            )}
            <Title className="entry-title">{post.title}</Title>
            <Box className="entry-meta">
              <Text>
                <span className="author">Written by {post.author?.first_name} {post.author?.last_name}</span>
              </Text>
            </Box>
            <Box className="entry-content">
              <SanitizeHtmlComponent htmlContent={post.content} />
            </Box>
            <Space h="md" />
            <Title order={5}>Related</Title>
            {relatedPosts.length > 0 && relatedPosts.map(({ id, slug, title }) => (
              <Grid key={id} style={{ marginBottom: "25px" }}>
                <Grid.Col span={8}>
                  <Link to={`/blog/${slug}/${id}`}>
                    <Title order={4}>{title}</Title>
                  </Link>
                </Grid.Col>
              </Grid>
            ))}
          </>
        )}
        {!isLoading && !error && !post && <Text color="yellow">Nothing to show</Text>}

        <PostInteraction post={post as BlogPost}/>
        <PostComment post={post as BlogPost}/>
      </Container>
    </>
  );
};

export default Post;